class LuxBestsellerProduct extends HTMLElement {
    constructor() {
        super();
        this.isDown = false;
        this.startX = 0;
        this.preventClick = false;
        
        // Internal state for gallery positioning
        this.index = 0;
        this.currentTranslate = 0;
        this.prevTranslate = 0;
    }

    connectedCallback() {
        this.header = this.querySelector('.lux-bestseller-product__header');
        this.gallery = this.querySelector('.lux-bestseller-product__gallery--inner');
        this.thumbs = this.querySelectorAll('.lux-bestseller-product__scrollbar-thumb');
        this.slides = this.querySelectorAll('.lux-bestseller-product__frame');
        this.swatches = this.querySelectorAll('.lux-bestseller-product__swatch');
        this.imageFeature = this.querySelector('.image-feature');
        
        if (this.gallery) {
            // Force hardware acceleration and layout for the gallery
            this.gallery.style.display = 'flex';
            this.gallery.style.willChange = 'transform';
            this.gallery.style.transition = 'none';
            
            this.initDragScroll();
            this.initScrollbar();
        }

        if (this.header) {
            this.header.addEventListener('click', (e) => {
                if (this.preventClick) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.preventClick = false; 
                }
            });
        }

        if (this.swatches.length) {
            this.swatches.forEach(swatch => {
                swatch.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.updateFeatureImage(swatch);
                });

                swatch.addEventListener('mouseenter', () => this.updateFeatureImage(swatch));
            });
        }
    }

    initScrollbar() {
        if (this.thumbs.length === 0 || this.slides.length === 0) return;

        const thumbWidth = 100 / this.slides.length;
        this.thumbs.forEach(thumb => {
            thumb.style.width = `${thumbWidth}%`;
            thumb.style.transition = 'transform 0.1s linear';
        });

        this.syncScrollbar();
    }

    syncScrollbar() {
        if (this.thumbs.length === 0 || this.slides.length === 0 || !this.slides[0]) return;

        const slideWidth = this.slides[0].offsetWidth;
        const maxTranslate = (this.slides.length - 1) * slideWidth;
        
        // Calculate percentage based on the absolute translation value
        const scrollPercentage = maxTranslate === 0 ? 0 : Math.abs(this.currentTranslate) / maxTranslate;
        
        // Translate the thumb within the scrollbar track (0 to (N-1)*100%)
        const maxTravel = (this.slides.length - 1) * 100;
        const translateX = Math.max(0, Math.min(scrollPercentage * maxTravel, maxTravel));

        this.thumbs.forEach(thumb => {
            thumb.style.transform = `translateX(${translateX}%)`;
        });
    }

    updateFeatureImage(swatch) {
        const imageURL = swatch.getAttribute('data-image');
        const variantURL = swatch.getAttribute('data-url');
        if (!imageURL || !variantURL || !this.gallery) return;

        this.swatches.forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');

        if (this.imageFeature) {
            this.imageFeature.src = imageURL;
            this.imageFeature.srcset = imageURL;

            const targetSlide = this.imageFeature.closest('.lux-bestseller-product__frame');

            if (targetSlide) {
                const allSlides = Array.from(this.slides);
                const targetIndex = allSlides.indexOf(targetSlide);

                // Update state variables
                this.index = targetIndex;
                const slideWidth = this.slides[0].offsetWidth;
                this.currentTranslate = this.index * -slideWidth;
                this.prevTranslate = this.currentTranslate;

                // Move gallery with smooth transition
                this.gallery.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.gallery.style.transform = `translateX(${this.currentTranslate}px)`;
                
                // Sync scrollbar immediately
                this.syncScrollbar();

                setTimeout(() => {
                    if (this.gallery) this.gallery.style.transition = 'none';
                }, 400);
            }
        }
        if (this.header) {
            this.header.href = variantURL;
        }
    }

    initDragScroll() {
        const slider = this.gallery;
        let touchStartX = 0;
        let touchStartY = 0;
        let isHorizontalMove = null;

        const getX = (e) => e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
        const getY = (e) => e.type.includes('mouse') ? e.pageY : e.touches[0].pageY;

        const onStart = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return;
            
            this.isDown = true;
            this.preventClick = false;
            touchStartX = getX(e);
            touchStartY = getY(e);
            isHorizontalMove = null;

            // Kill any active transitions for immediate response
            slider.style.transition = 'none';
        };

        const onMove = (e) => {
            if (!this.isDown) return;

            const currentX = getX(e);
            const currentY = getY(e);
            const diffX = currentX - touchStartX;
            const diffY = currentY - touchStartY;

            // Lock direction after initial movement threshold
            if (isHorizontalMove === null) {
                if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
                    isHorizontalMove = Math.abs(diffX) > Math.abs(diffY);
                }
            }

            if (isHorizontalMove === true) {
                // Moving horizontally: prevent page scroll and manual move gallery
                if (e.cancelable) e.preventDefault();
                
                const slideWidth = this.slides[0].offsetWidth;
                // Resistance clamping: prevent dragging physically past 1 slide width
                const clampedDiffX = Math.max(-slideWidth, Math.min(slideWidth, diffX));
                
                this.currentTranslate = this.prevTranslate + clampedDiffX;
                slider.style.transform = `translateX(${this.currentTranslate}px)`;
                
                this.syncScrollbar();

                if (Math.abs(diffX) > 10) {
                    this.preventClick = true;
                }
            }
        };

        const onEnd = () => {
            if (!this.isDown) return;
            this.isDown = false;

            const slideWidth = this.slides[0].offsetWidth;
            const movedBy = this.currentTranslate - this.prevTranslate;

            // Strict one-slide logic based on swipe distance
            const threshold = 50; 
            if (movedBy < -threshold && this.index < this.slides.length - 1) {
                this.index++;
            } else if (movedBy > threshold && this.index > 0) {
                this.index--;
            }

            // Finalize translation to snapped index
            this.currentTranslate = this.index * -slideWidth;
            this.prevTranslate = this.currentTranslate;

            // Animate snap
            slider.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            slider.style.transform = `translateX(${this.currentTranslate}px)`;
            
            this.syncScrollbar();

            // Clear transition after snap completes
            setTimeout(() => {
                slider.style.transition = 'none';
            }, 300);
        };

        // Event Bindings
        slider.addEventListener('touchstart', onStart, { passive: true });
        slider.addEventListener('mousedown', onStart);

        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('mousemove', onMove);

        window.addEventListener('touchend', onEnd);
        window.addEventListener('mouseup', onEnd);
    }
}

if (!customElements.get('lux-bestseller-product')) {
    customElements.define('lux-bestseller-product', LuxBestsellerProduct);
}